import { formatTimeInterval, parseTimeToSeconds, type TimeIntervalFormat } from '@/shared/lib';

/**
 * Таймкод в сохранённом тексте комментария: `[MM:SS]` или `[H:MM:SS]`.
 *
 * Единственный источник этой записи: по нему таймкод и распознаётся при показе комментария
 * (маркер в `ui/commentMarkers.ts`), и переводится здесь в действующий формат отображения.
 */
export const STORED_TIMECODE_PATTERN = /\[(\d{1,2}:\d{2}:\d{2}|\d{2}:\d{2})\]/;

/** Тот же таймкод для сплошного поиска по тексту — флаги регулярки у маркера свои */
const STORED_TIMECODE_SEARCH = new RegExp(STORED_TIMECODE_PATTERN.source, 'g');

/** Таймкод, набранный при правке: запись времени либо голое число секунд */
const TYPED_TIMECODE_PATTERN = /\[(\d{1,2}:\d{2}:\d{2}|\d{1,2}:\d{2}|\d+)\]/g;

/** Голое число секунд */
const SECONDS_ONLY_PATTERN = /^\d+$/;

/**
 * Показывает сохранённые таймкоды комментария в переданном формате отображения времени:
 * в формате `SECOND` — числом секунд от начала записи, в `TIME` — той же записью, что и хранятся.
 */
export const formatCommentTimecodes = (comment: string, format: TimeIntervalFormat): string => {
  if (format !== 'SECOND') return comment;

  return comment.replace(
    STORED_TIMECODE_SEARCH,
    (_marker, time: string) => `[${formatTimeInterval(parseTimeToSeconds(time), 'SECOND')}]`,
  );
};

/**
 * Приводит набранные пользователем таймкоды к записи хранения — `[MM:SS]`, а с целыми часами `[H:MM:SS]`.
 * Ею же таймкод ставит языковая модель, и она остаётся узнаваемой вне интерфейса, где формат
 * отображения неизвестен.
 *
 * Голое число секунд — запись формата `SECOND`: в `TIME` оно таймкодом не считается и остаётся текстом.
 */
export const normalizeCommentTimecodes = (comment: string, format: TimeIntervalFormat): string =>
  comment.replace(TYPED_TIMECODE_PATTERN, (marker, time: string) => {
    const isSecondsOnly = SECONDS_ONLY_PATTERN.test(time);
    if (isSecondsOnly && format !== 'SECOND') return marker;

    const totalSeconds = isSecondsOnly ? Number(time) : parseTimeToSeconds(time);
    return `[${formatTimeInterval(totalSeconds, 'TIME')}]`;
  });
